import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import BreadCrumb from "components/common/BreadCrumb";
import { Container } from "reactstrap";
import { toast } from "react-toastify";
import { getProductCategory } from "features/ProductCategories/services/ProductCategoryService";
import { ProductCategory } from "features/ProductCategories/types/product-category.types";
import { getDocumentTitle } from "utils/helpers";
import { ProductCategoryCreateModal } from "./ProductCategoryCreateModal";

/**
 * Deep-link entry for editing a category; uses the same modal as the list.
 */
const ProductCategoryForm = () => {
  document.title = getDocumentTitle("Edit product category");
  const navigate = useNavigate();
  const { id } = useParams();
  const [record, setRecord] = useState<ProductCategory | null>(null);
  const [loadingRecord, setLoadingRecord] = useState(true);

  useEffect(() => {
    if (!id) {
      setLoadingRecord(false);
      return;
    }
    let cancelled = false;
    const load = async () => {
      setLoadingRecord(true);
      try {
        const res = await getProductCategory(Number(id));
        if (!cancelled && res.success) {
          setRecord(res.data);
        } else if (!cancelled) {
          toast.error("Could not load category");
          navigate("/inventory-management/product-categories", {
            replace: true,
          });
        }
      } catch (err) {
        console.warn(err);
        if (!cancelled) {
          toast.error("Could not load category");
          navigate("/inventory-management/product-categories", {
            replace: true,
          });
        }
      } finally {
        if (!cancelled) setLoadingRecord(false);
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [id, navigate]);

  const listPath = "/inventory-management/product-categories";

  const handleClose = () => {
    navigate(listPath, { replace: true });
  };

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb
          title="Edit category"
          pageTitle="Inventory Management"
          backTo={{ label: "Product categories", path: listPath }}
        />
        {loadingRecord ? (
          <p className="text-muted">Loading…</p>
        ) : record ? (
          <ProductCategoryCreateModal
            show
            editingCategory={record}
            onClose={handleClose}
            onSuccess={handleClose}
          />
        ) : null}
      </Container>
    </div>
  );
};

export default ProductCategoryForm;
